import { isRegistrationClosed, scheduleRegistrationClosingRefresh } from "./registration-closing";
import { html, nothing, svg, type TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import { createActorState, type ActorStateOf } from "../state/actor-state";
import { ZipperEmbedElement } from "./base/zipper-embed-element";
import { eventsStyles } from "./events/events.styles";
import type {
  ComponentType,
  EmbedConfig,
  EventsOptions,
} from "../config/types";
import { MOCK_EVENTS, type MockEvent } from "./data/mock-events";
import {
  fetchEvents,
  fetchEventDescription,
  fetchMemberContext,
  fetchAppointmentForm,
  submitAppointmentForm,
  type EmbedForm,
  type FormSubmitIdentity,
} from "../data/live";
import { formatZonedTime } from "./zipper-schedule";
import { timezoneGroups, tzAbbrev } from "./timezones";
import {
  createGuestRoster,
  isAccountExistsError,
  registerGuest,
  registerSelf,
  type GuestRosterHandle,
} from "../payments/stripe-client";
import {
  accountExistsForOtherMessage,
  accountExistsMessage,
} from "../checkout/account-exists-copy";
import {
  emptyRequiredProfile,
  dateOfBirthError,
  isPhoneRequired,
  missingRequiredProfileFields,
  requiredProfilePayload,
  resolveRequiredSignupFields,
} from "../checkout/required-profile";
import { hasInvalidOptionalPhone, hasValidPhone, formatPhoneInput } from "../checkout/checkout-math";
import { CheckoutSubmissionController } from "../checkout/submission.machine";
import "./internal/zipper-payment";
import "./internal/zipper-filter-pill";
import "./internal/zipper-confirmation";
import "./internal/zipper-commerce-checkout";
import "./internal/zipper-booking-checkout";
import { openSignIn } from "./internal/open-signin";
import { clearEmbedToken } from "../config/client";
import type { ZipperPayment } from "./internal/zipper-payment";
import { runtimeApiArguments } from "../runtime/context";
import { renderEventCard, renderEventDetail } from "./events/events.views";
import { formatShortAmount } from "./format-money";
import { programCheckoutSubtitle } from "./programs/program.views";
import {
  CommerceOfferingKind,
  CommerceRequestedAction,
  type CommerceEventOfferingRefDto,
  type CommerceProgramOfferingRefDto,
} from "@zipper/customer-commerce-client";

const RATIO_MAP: Record<string, string> = {
  "1:1": "1 / 1",
  "4:3": "4 / 3",
  "3:2": "3 / 2",
  "16:9": "16 / 9",
};

const MONTHS_LONG: Record<string, string> = {
  Jan: "January",
  Feb: "February",
  Mar: "March",
  Apr: "April",
  May: "May",
  Jun: "June",
  Jul: "July",
  Aug: "August",
  Sep: "September",
  Oct: "October",
  Nov: "November",
  Dec: "December",
};

const ICON_CARET = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;
const ICON_DOC = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/></svg>`;
const ICON_CHECK = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;
const ICON_CLOCK = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;
const ICON_SIGNIN = svg`<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>`;

/** An extra attendee added at checkout, one per ticket beyond the buyer's own. */
type EventGuest = { firstName: string; lastName: string; email: string };

/**
 * Thrown after a registration failure has ALREADY been explained to the
 * customer, to unwind the rest of the ticket loop without the generic handler
 * replacing that explanation with "Registration failed. Please try again."
 */
class AlreadyReportedRegistrationError extends Error {}

const eventsActorState = createActorState(() => ({
  openId: null as string | null,
  typeFilter: "",
  locationFilter: "",
  instructorFilter: "",
  reg: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  },
  requiredProfile: emptyRequiredProfile(),
  qty: 1,
  guests: [] as EventGuest[],
  waiver: false,
  waiverOpen: false,
  waiverAttached: false,
  waiverForm: null as EmbedForm | null,
  evtStep: 1,
  evtDone: false,
  registrationError: "",
  events: [] as MockEvent[],
  catalogStatus: "idle" as "idle" | "loading" | "ready" | "error",
  signedInName: "",
  memberCtx: null as Awaited<ReturnType<typeof fetchMemberContext>>,
  descCache: {} as Record<string, string>,
  selectedTz: "",
  orgTimezone: "America/New_York",
  payReady: false,
}));

type EventsActorState = ActorStateOf<typeof eventsActorState>;

const EVT_WAIVER_PARAS = [
  "I, the undersigned, acknowledge that participation in the activities offered by the studio carries inherent risks of physical injury. I confirm that I am physically able to participate and have disclosed any conditions that may affect my safe practice.",
  "I voluntarily assume all risk of injury, loss, or damage to person or property arising from participation. I agree to follow all instructor and studio guidance.",
  "I release the studio, its instructors, employees, and contractors from any and all claims arising out of my participation, except where caused by gross negligence.",
  "This waiver is binding upon me, my heirs, and my legal representatives. I have read this waiver in full and understand its terms.",
];

/**
 * <zipper-events instance="..."> — event list + registration.
 * Ported from the handoff `events-widget.jsx`. Mock data for now.
 */
@customElement("zipper-events")
export class ZipperEvents extends ZipperEmbedElement {
  private readonly submission = new CheckoutSubmissionController(this, "event");

  static styles = eventsStyles;
  /** Host capability gate: older cached bundles must retain legacy calendar checkout. */
  static readonly calendarSelectionVersion = 1;

  @eventsActorState.field("openId")
  private openId!: EventsActorState["openId"];

  private get placed(): boolean {
    return this.submission.succeeded;
  }

  private get submitting(): boolean {
    return this.submission.busy;
  }
  @eventsActorState.field("typeFilter")
  private typeFilter!: EventsActorState["typeFilter"];

  @eventsActorState.field("locationFilter")
  private locationFilter!: EventsActorState["locationFilter"];

  @eventsActorState.field("instructorFilter")
  private instructorFilter!: EventsActorState["instructorFilter"];

  @eventsActorState.field("reg")
  private reg!: EventsActorState["reg"];

  @eventsActorState.field("requiredProfile")
  private requiredProfile!: EventsActorState["requiredProfile"];

  @eventsActorState.field("qty")
  private qty!: EventsActorState["qty"];

  @eventsActorState.field("guests")
  private guests!: EventsActorState["guests"];

  @eventsActorState.field("waiver")
  private waiver!: EventsActorState["waiver"];

  @eventsActorState.field("waiverOpen")
  private waiverOpen!: EventsActorState["waiverOpen"];

  /**
   * E3: true when the currently-open event's activity has a waiver-type form
   * attached (set synchronously from event data on open) — drives whether the
   * waiver block + its required gate render at all. `waiverForm` is the real
   * form content, fetched async once `waiverAttached` is true; until it
   * resolves (or if it resolves with no content), `renderEvtWaiver()` falls
   * back to the hardcoded copy rather than hiding the block, since a form IS
   * attached — see the `waiverRequired` getter and `EVT_WAIVER_PARAS`.
   */
  @eventsActorState.field("waiverAttached")
  private waiverAttached!: EventsActorState["waiverAttached"];

  @eventsActorState.field("waiverForm")
  private waiverForm!: EventsActorState["waiverForm"];

  @eventsActorState.field("evtStep")
  private evtStep!: EventsActorState["evtStep"];

  @eventsActorState.field("evtDone")
  private evtDone!: EventsActorState["evtDone"];

  @eventsActorState.field("registrationError")
  private registrationError!: EventsActorState["registrationError"];

  @eventsActorState.field("events")
  private events!: EventsActorState["events"];

  @eventsActorState.field("catalogStatus")
  private catalogStatus!: EventsActorState["catalogStatus"];

  private cancelClosingRefresh?: () => void;

  @eventsActorState.field("signedInName")
  private signedInName!: EventsActorState["signedInName"];

  @eventsActorState.field("memberCtx")
  private memberCtx!: EventsActorState["memberCtx"];

  /** Lazily-fetched event descriptions, keyed by event id (slug). */
  @eventsActorState.field("descCache")
  private descCache!: EventsActorState["descCache"];

  @eventsActorState.field("selectedTz")
  private selectedTz!: EventsActorState["selectedTz"];

  @eventsActorState.field("orgTimezone")
  private orgTimezone!: EventsActorState["orgTimezone"];

  private loadedAuthorityKey = "";
  private catalogRequest = 0;
  private selectionEpoch = 0;
  /**
   * Mirrors <zipper-payment>'s `canSubmit`, fed by its `zipper-pay-ready`
   * event. The card fields live in Stripe's cross-origin iframe, so this event
   * is the only way to know the embedded form is actually chargeable.
   */
  @eventsActorState.field("payReady")
  private payReady!: EventsActorState["payReady"];

  private paymentEl: ZipperPayment | null = null;

  protected get expectedType(): ComponentType {
    return "events";
  }

  protected willUpdate(changed: Map<string, unknown>): void {
    if ((changed.has("config") || changed.has("runtime")) && this.config) {
      const authorityKey = this.discoveryAuthorityKey();
      if (authorityKey === this.loadedAuthorityKey) return;
      this.loadedAuthorityKey = authorityKey;
      this.resetSelection();
      this.clearFilters();
      this.reg = { firstName: "", lastName: "", email: "", phone: "" };
      this.requiredProfile = emptyRequiredProfile();
      this.events = [];
      this.memberCtx = null;
      this.signedInName = "";
      this.descCache = {};
      void this.loadEvents(authorityKey);
      void this.loadMember(authorityKey);
    }
  }

  private discoveryAuthorityKey(): string {
    return JSON.stringify({
      orgId: this.config?.orgId ?? 0,
      options: this.config?.options ?? {},
      viewerRevision: this.runtime.viewerRevision ?? "guest",
      surface: this.runtime.surface,
      audience: this.runtime.audience,
      customer: { userId: this.runtime.customer?.userId, rosterId: this.runtime.customer?.rosterId },
    });
  }

  /** Load the current allowed catalog before honoring a host selection. */
  private async loadEvents(authorityKey: string): Promise<void> {
    if (!this.config) return;
    const requestId = ++this.catalogRequest;
    const current = () => requestId === this.catalogRequest && authorityKey === this.loadedAuthorityKey;
    this.catalogStatus = "loading";
    try {
      const live = this.config.orgId ? await fetchEvents(this.config.orgId) : MOCK_EVENTS;
      if (!current()) return;
      this.events = live;
      this.catalogStatus = "ready";
      this.orgTimezone = live.find((e) => e.timezone)?.timezone || this.orgTimezone;
      const initialId = this.opts.initialEventId;
      if (initialId && this.filteredEvents().some((event) => event.id === initialId)) {
        this.openEvent(initialId);
      }
      void this.hydrateDescriptions(live, authorityKey);
    } catch {
      if (!current()) return;
      this.events = [];
      this.catalogStatus = "error";
    }
  }

  /**
   * The bulk events endpoint blanks each event's description (contentHtml), so
   * live cards and the checkout render without their "About this event" copy.
   * Fetch the real description per event on demand (the same public endpoint
   * the web app uses) and cache it by id so both the card blurb and the detail
   * view fill in. Mock events already carry a summary, so they are skipped.
   */
  private async hydrateDescriptions(
    events: MockEvent[],
    authorityKey: string,
  ): Promise<void> {
    const cfg = this.config;
    const requestId = this.catalogRequest;
    if (!cfg) return;
    await Promise.all(
      events
        .filter((e) => !e.summary && !this.descCache[e.id])
        .map(async (e) => {
          try {
            const text = await fetchEventDescription(cfg.orgId, e.id);
            if (text && authorityKey === this.loadedAuthorityKey && requestId === this.catalogRequest) {
              this.descCache = { ...this.descCache, [e.id]: text };
            }
          } catch {
            /* description is optional — leave the card without a blurb */
          }
        }),
    );
  }

  private setQty(n: number): void {
    const next = Math.max(1, Math.min(10, n));
    this.qty = next;
    const guests = this.guests.slice(0, next - 1);
    while (guests.length < next - 1)
      guests.push({ firstName: "", lastName: "", email: "" });
    this.guests = guests;
  }

  private get opts(): EventsOptions {
    return (this.config?.options as EventsOptions) ?? {};
  }

  private get requirePhone(): boolean {
    // Note the polarity: unlike every other surface, events default this ON
    // (`!== false`). ORing the org policy in can only ever keep it on.
    return isPhoneRequired(
      this.opts.requirePhone !== false,
      this.config?.requiredSignupFields,
    );
  }

  /**
   * Whether this registration provisions its own roster through `create-guest`.
   * A signed-in member already has one, so the org's required-signup-fields
   * policy has nothing to attach to and is not re-asked of them.
   */
  private get isGuestCheckout(): boolean {
    return !this.memberCtx;
  }

  private async loadMember(authorityKey: string): Promise<void> {
    if (!this.config?.orgId) return;
    const ctx = await fetchMemberContext(this.config.orgId);
    if (authorityKey !== this.loadedAuthorityKey) return;
    this.memberCtx = ctx;
    if (!ctx) {
      this.signedInName = "";
      return;
    }
    this.signedInName = ctx.firstName?.trim() || "there";
    if (!this.reg.email && (ctx.email || ctx.firstName)) {
      this.reg = {
        ...this.reg,
        firstName: this.reg.firstName || ctx.firstName || "",
        lastName: this.reg.lastName || ctx.lastName || "",
        email: this.reg.email || ctx.email || "",
        phone: this.reg.phone || (ctx.phone ? formatPhoneInput(ctx.phone) : ""),
      };
    }
  }

  private get viewerTz(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch {
      return "";
    }
  }

  private get displayTz(): string {
    return this.selectedTz || this.orgTimezone || this.viewerTz;
  }

  /** Short zone label, e.g. "EDT" — shared with every other embed picker so
   * the confirmation's abbreviation can't disagree with the schedule's. */
  private tzAbbrev(tz: string): string {
    return tzAbbrev(tz);
  }

  private eventTimes(ev: MockEvent): { start: string; end: string } {
    const tz = this.displayTz;
    return {
      start: formatZonedTime(ev.startIso, tz, ev.start),
      end: formatZonedTime(ev.endIso, tz, ev.end),
    };
  }

  private renderTzPicker(): TemplateResult {
    const tz = this.displayTz;
    // Every zone the engine knows, U.S. first, with the studio's and the
    // visitor's own pinned at the top — a visitor abroad could previously only
    // read these times in one of four U.S. zones.
    const groups = timezoneGroups([
      { tz: this.orgTimezone, label: "Studio time" },
      { tz: this.viewerTz, label: "Your time zone" },
    ]);
    return html`<div class="tz-row">
      <div class="tz-pill">
        ${ICON_CLOCK}
        <select
          aria-label="Time zone"
          .value=${tz}
          @change=${(e: Event) =>
            (this.selectedTz = (e.target as HTMLSelectElement).value)}
        >
          ${groups.map(
            (g) => html`<optgroup label=${g.label}>
              ${g.zones.map(
                (o) => html`<option value=${o.tz}>${o.label}</option>`,
              )}
            </optgroup>`,
          )}
        </select>
      </div>
    </div>`;
  }

  private logout(): void {
    if (this.config?.orgId) clearEmbedToken(this.config.orgId);
    this.memberCtx = null;
    this.signedInName = "";
  }

  protected renderContent(_config: EmbedConfig): TemplateResult {
    const open = this.events.find((e) => e.id === this.openId);
    if (this.opts.initialEventId && !open) {
      return html`<div class="wrap">
        ${this.catalogStatus === "idle" || this.catalogStatus === "loading"
          ? html`<p role="status">Loading this event…</p>`
          : this.catalogStatus === "error"
            ? html`<p role="alert">Unable to load this event. Please try again.</p>
                <button class="reg" @click=${() => void this.loadEvents(this.loadedAuthorityKey)}>Try again</button>`
            : html`<p role="status">This event is no longer available. Close this view and choose another event.</p>`}
      </div>`;
    }
    return html`<div class="wrap">
      ${open ? this.renderDetail(open) : this.renderList()}
    </div>`;
  }

  private resetSelection(): void {
    this.selectionEpoch++;
    this.teardownPayment();
    this.openId = null;
    this.evtStep = 1;
    this.evtDone = false;
    this.submission.reset();
    this.setQty(1);
    this.waiver = false;
    this.waiverOpen = false;
    this.waiverAttached = false;
    this.waiverForm = null;
    this.registrationError = "";
  }

  private openEvent(id: string): void {
    this.resetSelection();
    this.openId = id;
    const ev = this.events.find((e) => e.id === id);
    this.waiverAttached = !!ev?.forms?.some((f) => f.type === "waiver");
    if (this.waiverAttached && ev) void this.loadEventWaiver(ev);
  }

  private closeEvent(): void {
    const eventId = this.openId;
    this.catalogRequest++;
    this.resetSelection();
    if (this.runtime.surface === "dashboard-customer" && this.opts.initialEventId) {
      this.dispatchEvent(new CustomEvent("zipper-event-close", {
        detail: { eventId }, bubbles: true, composed: true,
      }));
    }
  }

  /**
   * E3: fetch the real content of the waiver form attached to this event's
   * activity (same public form endpoint the schedule checkout uses) and
   * cache it on `waiverForm` for `renderEvtWaiver()`. Best-effort: on
   * failure/empty content the block still renders (waiverAttached stays
   * true) but falls back to the hardcoded EVT_WAIVER_PARAS copy.
   */
  private async loadEventWaiver(ev: MockEvent): Promise<void> {
    const orgId = this.config?.orgId;
    const authorityKey = this.loadedAuthorityKey;
    const selectionEpoch = this.selectionEpoch;
    const waiverDef = ev.forms?.find((f) => f.type === "waiver");
    if (!orgId || !waiverDef) return;
    try {
      const form = await fetchAppointmentForm(orgId, waiverDef.formId);
      // The user may have navigated to a different event while this was in
      // flight — only apply the result if we're still looking at this one.
      if (this.openId === ev.id && authorityKey === this.loadedAuthorityKey && selectionEpoch === this.selectionEpoch) this.waiverForm = form;
    } catch {
      /* leave waiverForm null — renderEvtWaiver() falls back to hardcoded copy */
    }
  }

  /** E3: whether this event's checkout should show + gate on the waiver block. */
  private get waiverRequired(): boolean {
    return this.waiverAttached;
  }

  private filteredEvents(): MockEvent[] {
    const o = this.opts;
    // ST1: embed-level pre-filter (set in settings) narrows the source before
    // the visitor's own pills apply. The settings UI stores IDs — event-grouping
    // ids, host user ids, and location ids — so match by those. Empty/absent
    // array = pass-through, mirroring the schedule's `filtered()`.
    const preEvent = o.filterEventIds?.length
      ? new Set(o.filterEventIds)
      : null;
    const preInst = o.filterInstructors?.length
      ? new Set(o.filterInstructors)
      : null;
    const preLoc = o.filterLocations?.length
      ? new Set(o.filterLocations)
      : null;
    return this.events.filter(
      (e) =>
        // Match the event's event-grouping id (falling back to its own id when a
        // row has no grouping id — e.g. mock rows), the host provider user ids
        // (compared as strings), and the location id.
        (!preEvent || preEvent.has(e.eventGroupingId ?? e.id)) &&
        (!preInst ||
          !!e.providerUserIds?.some((id) => preInst.has(String(id)))) &&
        (!preLoc || (!!e.locationId && preLoc.has(e.locationId))) &&
        (!this.typeFilter || e.type === this.typeFilter) &&
        (!this.locationFilter || e.location === this.locationFilter) &&
        (!this.instructorFilter || e.instructor === this.instructorFilter),
    );
  }

  private clearFilters(): void {
    this.typeFilter = "";
    this.locationFilter = "";
    this.instructorFilter = "";
  }

  private renderFilters(): TemplateResult {
    const types = [...new Set(this.events.map((e) => e.type))];
    const locations = [...new Set(this.events.map((e) => e.location))];
    const instructors = [...new Set(this.events.map((e) => e.instructor))];
    const pill = (
      value: string,
      allLabel: string,
      options: string[],
      onChange: (v: string) => void,
    ) => html`
      <zipper-filter-pill
        .value=${value}
        .label=${allLabel}
        .options=${options}
        @zipper-filter-change=${(e: CustomEvent<{ value: string }>) =>
          onChange(e.detail.value)}
      ></zipper-filter-pill>
    `;
    return html`
      <div class="filters">
        ${pill(
          this.typeFilter,
          "All events",
          types,
          (v) => (this.typeFilter = v),
        )}
        ${pill(
          this.locationFilter,
          "All locations",
          locations,
          (v) => (this.locationFilter = v),
        )}
        ${pill(
          this.instructorFilter,
          "All instructors",
          instructors,
          (v) => (this.instructorFilter = v),
        )}
      </div>
    `;
  }

  private renderList(): TemplateResult {
    const o = this.opts;
    const ratio = RATIO_MAP[o.thumbnailRatio ?? "3:2"];
    const events = this.filteredEvents();
    return html`
      ${this.renderTzPicker()} ${o.hideFilters ? nothing : this.renderFilters()}
      ${events.length === 0
        ? html`<div class="empty">
            No events match these filters.
            <button @click=${() => this.clearFilters()}>Clear filters</button>
          </div>`
        : html`<div class="list">
            ${this.withMonthDividers(events).map((item) =>
              item.divider
                ? html`<div class="month-divider">
                    <span class="lbl">${item.divider}</span>
                    <span class="rule"></span>
                  </div>`
                : this.renderCard(item.event!, ratio),
            )}
          </div>`}
    `;
  }

  /** Interleave month-divider markers ("May 2026") before each new month group. */
  private withMonthDividers(
    events: MockEvent[],
  ): Array<{ divider?: string; event?: MockEvent }> {
    const out: Array<{ divider?: string; event?: MockEvent }> = [];
    let lastKey = "";
    for (const ev of events) {
      const key = `${ev.month} ${ev.year}`;
      if (key !== lastKey) {
        out.push({
          divider: `${MONTHS_LONG[ev.month] ?? ev.month} ${ev.year}`,
        });
        lastKey = key;
      }
      out.push({ event: ev });
    }
    return out;
  }

  private renderCard(ev: MockEvent, ratio?: string): TemplateResult {
    const times = this.eventTimes(ev);
    return renderEventCard(ev, {
      options: this.opts,
      registrationClosed: !ev.program && !ev.externalTickets && isRegistrationClosed(ev),
      ...(ratio ? { thumbnailRatio: ratio } : {}),
      description: ev.summary || this.descCache[ev.id] || "",
      startTime: times.start,
      endTime: times.end,
      timezoneAbbreviation: this.tzAbbrev(this.displayTz),
      currency: this.config?.currency,
      onOpen: () => this.openEvent(ev.id),
    });
  }

  private renderDetail(ev: MockEvent): TemplateResult {
    const times = this.eventTimes(ev);
    return renderEventDetail(ev, {
      registrationClosed: !ev.program && !ev.externalTickets && isRegistrationClosed(ev),
      startTime: times.start,
      endTime: times.end,
      timezoneAbbreviation: this.tzAbbrev(this.displayTz),
      description: ev.description || ev.summary || this.descCache[ev.id] || "",
      registration: this.renderRegister(ev),
      onBack: () => this.closeEvent(),
      onScrollToRegistration: (event) => this.scrollToRegister(event),
      onShare: () => this.shareEvent(ev),
      onDownloadCalendar: () => this.downloadCalendar(ev),
    });
  }

  private shareEvent(ev: MockEvent): void {
    const url =
      (typeof window !== "undefined" && window.location.href.split("#")[0]) ||
      "";
    const text = `${ev.title}${ev.start ? ` — ${ev.start}` : ""}`;
    if (navigator.share) {
      void navigator
        .share({ title: ev.title, text, url })
        .catch(() => undefined);
      return;
    }
    void navigator.clipboard?.writeText(url ? `${text} ${url}` : text);
  }

  private downloadCalendar(ev: MockEvent): void {
    const start = ev.startIso ? new Date(ev.startIso) : new Date();
    const end = ev.endIso
      ? new Date(ev.endIso)
      : ev.startIso && ev.end
        ? new Date(new Date(ev.startIso).getTime() + 60 * 60 * 1000)
        : new Date(start.getTime() + 60 * 60 * 1000);
    const fmt = (d: Date) =>
      d
        .toISOString()
        .replace(/[-:]/g, "")
        .replace(/\.\d{3}Z/, "Z");
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `DTSTART:${fmt(start)}`,
      `DTEND:${fmt(end)}`,
      `SUMMARY:${ev.title}`,
      `LOCATION:${ev.location}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${ev.id}.ics`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  private scrollToRegister(e: Event): void {
    e.preventDefault();
    void this.updateComplete.then(() => {
      this.renderRoot
        .querySelector(".register")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  /** Inline single-page checkout below the event detail (Your details → Payment). */
  private renderRegister(ev: MockEvent): TemplateResult {
    if (ev.externalTickets) {
      return html`<div class="register">
        <div class="divider"></div>
        ${ev.externalTicketUrl
          ? html`<a
              class="reg"
              href=${ev.externalTicketUrl}
              target="_blank"
              rel="noopener noreferrer"
            >Buy tickets on the seller site</a>`
          : html`<p>Ticket link unavailable</p>`}
      </div>`;
    }

    if (!ev.program && !this.placed && !this.submission.paymentCommitted && !this.submission.busy && isRegistrationClosed(ev)) {
      const ended = ev.endIso && new Date(ev.endIso).getTime() <= Date.now();
      return html`<div class="register"><p role="status">${ended ? "Event has ended" : "Registration closed"}</p></div>`;
    }

    if (
      this.runtime.surface === "dashboard-customer" &&
      this.runtime.commerce &&
      ev.program
    ) {
      const offering: CommerceProgramOfferingRefDto = {
        kind: CommerceOfferingKind.Program,
        offeringKey: `program:${ev.program.programKey}`,
        sessionKeys: ev.program.sessionKeys,
        programName: ev.title,
        sessions: ev.program.sessions,
      };
      return html`<div class="register">
        <div class="divider"></div>
        <zipper-commerce-checkout
          .runtime=${this.runtime}
          .offering=${offering}
          .requestedAction=${CommerceRequestedAction.Book}
          .requestedTargetKey=${this.opts.initialTargetKey ?? null}
          .orgId=${this.config?.orgId ?? 0}
          .title=${ev.title}
          .subtitle=${programCheckoutSubtitle(ev)}
          .quantity=${this.qty}
          .stripe=${this.config?.stripe}
          @zipper-commerce-success=${() =>
            this.dispatchEvent(
              new CustomEvent("zipper-register-complete", {
                detail: {
                  eventId: ev.program?.programKey ?? ev.id,
                  title: ev.title,
                  tickets: this.qty,
                },
                bubbles: true,
                composed: true,
              }),
            )}
        ></zipper-commerce-checkout>
      </div>`;
    }
    if (
      this.runtime.surface === "dashboard-customer" &&
      this.runtime.commerce &&
      ev.activityId
    ) {
      const offering: CommerceEventOfferingRefDto = {
        kind: CommerceOfferingKind.Event,
        offeringKey: `event:${ev.id}`,
        activityKey: ev.activityId,
      };
      return html`<div class="register">
        <div class="divider"></div>
        <zipper-commerce-checkout
          .runtime=${this.runtime}
          .offering=${offering}
          .requestedAction=${CommerceRequestedAction.Book}
          .requestedTargetKey=${this.opts.initialTargetKey ?? null}
          .orgId=${this.config?.orgId ?? 0}
          .title=${ev.title}
          .subtitle=${`${this.eventTimes(ev).start} · ${ev.location}`}
          .quantity=${this.qty}
          .stripe=${this.config?.stripe}
          @zipper-commerce-success=${() =>
            this.dispatchEvent(
              new CustomEvent("zipper-register-complete", {
                detail: {
                  eventId: ev.id,
                  title: ev.title,
                  tickets: this.qty,
                },
                bubbles: true,
                composed: true,
              }),
            )}
        ></zipper-commerce-checkout>
      </div>`;
    }
    if (ev.requiresEntitlement && ev.activityId) {
      // PLAT-6994: membership / credit-only events have no drop-in to charge
      // and no free path, so the ticket flow below can't place them. Book them
      // the way the schedule books a class — pick a membership or credit pack.
      const times = this.eventTimes(ev);
      return html`<div class="register">
        <div class="divider"></div>
        <zipper-booking-checkout
          .className=${ev.title}
          .classWhen=${`${ev.weekday}, ${ev.month} ${ev.day} · ${times.start} – ${times.end} ${this.tzAbbrev(this.displayTz)}`}
          .instructorName=${ev.instructor}
          .image=${ev.image}
          .bookButtonText=${this.opts.buttonText || "Register"}
          .tiers=${ev.tiers ?? []}
          .stripe=${this.config?.stripe}
          .orgId=${this.config?.orgId ?? 0}
          .accent=${this.config?.theme?.accent ?? "#4F9DB5"}
          .requirePhone=${this.requirePhone}
          .requiredSignupFields=${this.config?.requiredSignupFields}
          .currency=${this.config?.currency ?? ""}
          .startIso=${ev.startIso ?? ""}
          .endIso=${ev.endIso ?? ""}
          .orgSlug=${this.config?.orgSlug ?? ""}
          .eventSlug=${ev.id}
          .activityId=${ev.activityId}
          .classForms=${ev.forms ?? []}
          @zipper-booking-close=${() => (this.openId = null)}
        ></zipper-booking-checkout>
      </div>`;
    }
    if (this.placed) {
      return html`<div class="register">
        <div class="divider"></div>
        <zipper-confirmation
          kind="class"
          .accent=${this.config?.theme?.accent ?? "#4F9DB5"}
          .orgId=${this.config?.orgId ?? 0}
          .email=${this.reg.email}
          .firstName=${this.reg.firstName}
          .lastName=${this.reg.lastName}
          .cTitle=${ev.title}
          .subline=${`${this.qty} ticket${this.qty === 1 ? "" : "s"}`}
          .price=${ev.price > 0
            ? formatShortAmount(ev.price * this.qty, this.config?.currency)
            : "Free"}
          .hideSetupAccount=${!!this.memberCtx}
          .hideBookAnother=${true}
        ></zipper-confirmation>
        <div style="text-align:center;margin-top:1rem">
          <button class="reg" @click=${() => this.closeEvent()}>
            Back to events
          </button>
        </div>
      </div>`;
    }
    const isFree = ev.price === 0;
    const ticketWord = this.qty === 1 ? "ticket" : "tickets";
    return html`
      <div class="register">
        <div class="divider"></div>
        <div class="rh-row">
          <h2 class="rh">Register</h2>
          ${this.renderSignInCta()}
        </div>
        <div class="reg-grid">
          <div class="steps">
            ${this.renderEvtStep1(ev)}
            ${this.renderEvtStep2(ev, isFree, ticketWord)}
          </div>
          ${this.renderEvtSummary(ev, isFree, ticketWord)}
        </div>
      </div>
    `;
  }

  /**
   * Date of birth / emergency contact, when the ORG requires them (PLAT-4456).
   * Guests only — a signed-in member's roster already carries this data.
   *
   * Takes the caller's `efield` so these inputs are built by the same factory
   * as the rest of the details step and cannot drift from it.
   */
  private renderRequiredProfileFields(
    efield: (
      label: string,
      value: string,
      type: string,
      on: (e: Event) => void,
      required?: boolean,
    ) => TemplateResult,
  ): TemplateResult | typeof nothing {
    if (!this.isGuestCheckout) return nothing;
    const required = resolveRequiredSignupFields(
      this.config?.requiredSignupFields,
    );
    const dobError = this.requiredProfile.dateOfBirth
      ? dateOfBirthError(this.config?.requiredSignupFields, this.requiredProfile.dateOfBirth)
      : null;
    if (!required.dateOfBirth && !required.emergencyContact) return nothing;
    const patch =
      (field: keyof typeof this.requiredProfile) => (e: Event) => {
        this.requiredProfile = {
          ...this.requiredProfile,
          [field]: (e.target as HTMLInputElement).value,
        };
      };
    return html`
      ${required.dateOfBirth
        ? html`<div class="grid2">
            ${efield(
              "Date of birth",
              this.requiredProfile.dateOfBirth,
              "date",
              patch("dateOfBirth"),
              true,
            )}
          </div>`
        : nothing}
      ${dobError ? html`<p role="alert" style="color:#b91c1c;font-size:0.8rem">${dobError}</p>` : nothing}
      ${required.emergencyContact
        ? html`<div class="grid2">
            ${efield(
              "Emergency contact name",
              this.requiredProfile.emergencyContactName,
              "text",
              patch("emergencyContactName"),
              true,
            )}
            ${efield(
              "Emergency contact phone",
              this.requiredProfile.emergencyContactPhone,
              "tel",
              patch("emergencyContactPhone"),
              true,
            )}
          </div>`
        : nothing}
    `;
  }

  private registrationValid(ev: MockEvent): boolean {
    const r = this.reg;
    const guestEmailRequired = !!ev.forms?.length;
    const guestsOk = this.guests.every(
      (g) =>
        g.firstName.trim() &&
        g.lastName.trim() &&
        (!guestEmailRequired || /\S+@\S+\.\S+/.test(g.email.trim())),
    );
    // EV5: when the org opted into "require phone", a valid phone must be
    // entered here (Your details) before Continue is enabled — so the phone is
    // collected once up front and no longer injected into the card lightbox.
    const phoneOk = this.requirePhone
      ? hasValidPhone(r.phone)
      : !hasInvalidOptionalPhone(r.phone);
    // PLAT-4456: the org can require a DOB / emergency contact before someone
    // joins. Guest registration mints the roster itself, so the API checks this
    // at `create-guest` — collect it here or the customer is rejected after
    // filling in everything else.
    const profileOk =
      !this.isGuestCheckout ||
      missingRequiredProfileFields(
        this.config?.requiredSignupFields,
        this.requiredProfile,
      ).length === 0;
    // E3: only gate on the waiver checkbox when this event actually has a
    // waiver form attached — no attached form ⇒ no required-form gate.
    const waiverOk = !this.waiverRequired || this.waiver;
    return !!(
      r.firstName.trim() &&
      r.lastName.trim() &&
      /\S+@\S+\.\S+/.test(r.email) &&
      waiverOk &&
      guestsOk &&
      phoneOk &&
      profileOk
    );
  }

  /**
   * Open the shared customer sign-in modal and refresh the member context once
   * the customer is in. Single owner of the sign-in entry point for this embed
   * so the headline CTA can never drift from the old inline link's behaviour.
   */
  private openSignInModal(): void {
    openSignIn(
      this.config?.orgId ?? 0,
      this.config?.theme?.accent ?? "#4F9DB5",
      (name) => {
        this.signedInName = name || "there";
        void this.loadMember(this.loadedAuthorityKey);
      },
    );
  }

  /**
   * 7/25 QA: the sign-in affordance is a real (secondary) button on the
   * "Register" headline row, not a link trailing the microcopy inside step 1.
   * Signed-out only — once the customer is in, {@link renderSignInBanner}
   * carries the "Signed in as …" status + Log out at the top of Your details,
   * where it explains the prefilled fields.
   */
  private renderSignInCta(): TemplateResult | typeof nothing {
    if (this.signedInName) return nothing;
    return html`<div class="signin-cta">
      <span class="signin-cta-t">Already a customer?</span>
      <button
        type="button"
        class="zui-btn-secondary"
        title="Sign in to prefill your details and pay with a card on file"
        @click=${() => this.openSignInModal()}
      >
        ${ICON_SIGNIN} Sign in
      </button>
    </div>`;
  }

  /** Signed-in status + Log out, above the prefilled Your details fields. */
  private renderSignInBanner(): TemplateResult | typeof nothing {
    if (!this.signedInName) return nothing;
    return html`<div class="ssum" style="margin-bottom:0.5rem">
      Signed in as ${this.signedInName}.
      <button type="button" class="edit" @click=${() => this.logout()}>
        Log out
      </button>
    </div>`;
  }

  private renderEvtStep1(ev: MockEvent): TemplateResult {
    void ev;
    const open = this.evtStep === 1;
    const set = (k: keyof typeof this.reg) => (e: Event) => {
      const v = (e.target as HTMLInputElement).value;
      this.reg = {
        ...this.reg,
        [k]: k === "phone" ? formatPhoneInput(v) : v,
      };
    };
    const setGuest =
      (i: number, k: "firstName" | "lastName" | "email") => (e: Event) =>
        (this.guests = this.guests.map((g, idx) =>
          idx === i ? { ...g, [k]: (e.target as HTMLInputElement).value } : g,
        ));
    const efield = (
      label: string,
      value: string,
      type: string,
      on: (e: Event) => void,
      required = false,
    ) =>
      html`<label class="efield"
        >${label}${required ? " *" : ""}<input
          type=${type}
          .value=${value}
          @input=${on}
      /></label>`;
    const summary = this.evtDone
      ? `${`${this.reg.firstName} ${this.reg.lastName}`.trim() || "You"} · ${this.qty} ${this.qty === 1 ? "ticket" : "tickets"}`
      : "";
    return html`
      <div class="estep ${open ? "open" : ""}">
        <div
          class="estep-head"
          @click=${() => this.evtDone && (this.evtStep = 1)}
        >
          <span
            class="num ${open ? "open" : ""} ${this.evtDone && !open
              ? "done"
              : ""}"
            >${this.evtDone && !open ? ICON_CHECK : 1}</span
          >
          <span class="st">Your details</span>
          ${this.evtDone && !open
            ? html`<button class="edit" @click=${() => (this.evtStep = 1)}>
                Edit
              </button>`
            : nothing}
        </div>
        ${this.evtDone && !open
          ? html`<div class="ssum">${summary}</div>`
          : nothing}
        ${open
          ? html`<div class="estep-body">
              ${this.renderSignInBanner()}
              <div class="grid2">
                ${efield(
                  "First name",
                  this.reg.firstName,
                  "text",
                  set("firstName"),
                  true,
                )}
                ${efield(
                  "Last name",
                  this.reg.lastName,
                  "text",
                  set("lastName"),
                  true,
                )}
              </div>
              <div class="grid2">
                ${efield("Email", this.reg.email, "email", set("email"), true)}
                ${efield(
                  this.requirePhone ? "Phone" : "Phone (optional)",
                  this.reg.phone,
                  "tel",
                  set("phone"),
                  this.requirePhone,
                )}
              </div>
              ${this.renderRequiredProfileFields(efield)}
              <div class="ticket-row">
                <div>
                  <div class="t">Tickets</div>
                  <div class="s">Buying for more than one?</div>
                </div>
                <div class="qstep">
                  <button @click=${() => this.setQty(this.qty - 1)}>−</button>
                  <span class="n">${this.qty}</span>
                  <button @click=${() => this.setQty(this.qty + 1)}>+</button>
                </div>
              </div>
              ${this.guests.length
                ? html`<div class="guests-h">Additional guests</div>
                    ${this.guests.map(
                      (g, i) =>
                        html`<div class="guest-card">
                          <div class="gh">Guest ${i + 1}</div>
                          <div class="grid2">
                            ${efield(
                              "First name",
                              g.firstName,
                              "text",
                              setGuest(i, "firstName"),
                              true,
                            )}
                            ${efield(
                              "Last name",
                              g.lastName,
                              "text",
                              setGuest(i, "lastName"),
                              true,
                            )}
                          </div>
                          ${efield(
                            ev.forms?.length ? "Email" : "Email (optional)",
                            g.email,
                            "email",
                            setGuest(i, "email"),
                            !!ev.forms?.length,
                          )}
                        </div>`,
                    )}`
                : nothing}
              ${this.waiverRequired ? this.renderEvtWaiver() : nothing}
              ${this.reg.phone.trim() && !hasValidPhone(this.reg.phone)
                ? html`<div role="alert" class="zui-error">
                    Enter a valid phone number; include + and a country code if needed.
                  </div>`
                : nothing}
              <button
                class="cta"
                ?disabled=${!this.registrationValid(ev)}
                @click=${() => {
                  this.evtDone = true;
                  this.evtStep = 2;
                }}
              >
                Continue
              </button>
            </div>`
          : nothing}
      </div>
    `;
  }

  private renderEvtWaiver(): TemplateResult {
    // E3: render the real content of the form attached to this event
    // (`waiverForm.details`, the org-authored waiver body text). The
    // hardcoded EVT_WAIVER_PARAS is only a fallback for the (attached-but-
    // empty-content / still-loading) case — never shown when no form is
    // attached at all, since this whole block is skipped by `waiverRequired`.
    const title = this.waiverForm?.name || "Liability waiver";
    const paras = this.waiverForm?.details?.trim()
      ? [this.waiverForm.details]
      : EVT_WAIVER_PARAS;
    return html`
      <div class="ewaiver">
        <button
          class="ewaiver-head"
          @click=${() => (this.waiverOpen = !this.waiverOpen)}
        >
          <span class="ic">${ICON_DOC}</span>
          <span style="flex:1;min-width:0">
            <span class="wt" style="display:block">${title}</span>
            <span class="ws"
              >${this.waiverOpen
                ? "Tap to collapse"
                : "Tap to read full waiver"}</span
            >
          </span>
          <span class="chev ${this.waiverOpen ? "open" : ""}"
            >${ICON_CARET}</span
          >
        </button>
        ${this.waiverOpen
          ? html`<div class="ewaiver-body">
              ${paras.map((p) => html`<p>${p}</p>`)}
            </div>`
          : nothing}
        <label class="ewaiver-check">
          <input
            type="checkbox"
            .checked=${this.waiver}
            @change=${(e: Event) =>
              (this.waiver = (e.target as HTMLInputElement).checked)}
          />
          I've read and agree to the liability waiver. I'm participating at my
          own risk and have no conditions that prevent safe practice.
        </label>
      </div>
    `;
  }

  private renderEvtStep2(
    ev: MockEvent,
    isFree: boolean,
    ticketWord: string,
  ): TemplateResult {
    const open = this.evtStep === 2;
    const total = ev.price * this.qty;
    return html`
      <div class="estep ${open ? "open" : ""}">
        <div
          class="estep-head"
          @click=${() => this.evtDone && (this.evtStep = 2)}
        >
          <span class="num ${open ? "open" : ""}">2</span>
          <span class="st">${isFree ? "Confirm registration" : "Payment"}</span>
        </div>
        ${open
          ? html`<div class="estep-body">
              ${isFree
                ? html`<div class="free-note">
                    This event is free — no payment needed. Confirm to reserve
                    your ${this.qty} ${ticketWord}.
                  </div>`
                : html`<div id="inline-pay-host"></div>`}
              ${this.registrationError
                ? html`<div role="alert" class="zui-error">
                    ${this.registrationError}
                  </div>`
                : nothing}
              <button
                class="cta block"
                ?disabled=${this.submitting || (!isFree && !this.payReady)}
                aria-busy=${this.submitting ? "true" : "false"}
                @click=${() => this.submitRegistration(ev)}
              >
                ${this.submitting
                  ? "Processing…"
                  : isFree
                    ? `Complete registration · ${this.qty} ${ticketWord}`
                    : `Pay ${formatShortAmount(total, this.config?.currency)}`}
              </button>
            </div>`
          : nothing}
      </div>
    `;
  }

  private renderEvtSummary(
    ev: MockEvent,
    isFree: boolean,
    ticketWord: string,
  ): TemplateResult {
    const total = ev.price * this.qty;
    const money = (amount: number) =>
      formatShortAmount(amount, this.config?.currency);
    return html`
      <aside class="esummary">
        <h3>Order Summary</h3>
        <div class="esum-li">
          <span>${ev.title}</span>
        </div>
        <div class="esum-li">
          <span
            >${isFree
              ? "Free admission"
              : `${money(ev.price)} × ${this.qty} ${ticketWord}`}</span
          >
          <span>${isFree ? "Free" : money(total)}</span>
        </div>
        <div class="esum-total">
          <span>Total</span><span>${isFree ? "Free" : money(total)}</span>
        </div>
      </aside>
    `;
  }

  /**
   * PLAT-5624: guarded — a second click used to either place the free
   * registration twice or append a SECOND <zipper-payment> overlay (two live
   * card forms, two chargeable sessions). The claim is synchronous, so it
   * lands before Lit can re-render the now-disabled CTA.
   *
   * The paid branch stays claimed while an overlay is mounted: releasing it
   * would let another overlay be created on top of the first.
   */
  private submitRegistration(ev: MockEvent): void {
    if (!ev.program && !ev.externalTickets && !this.submission.paymentCommitted && !this.submission.busy && isRegistrationClosed(ev)) {
      this.registrationError = "Registration is closed.";
      this.requestUpdate();
      return;
    }
    // The form is embedded now, so the CTA is a submit — not an opener.
    if (this.paymentEl) {
      if (!this.submission.claim()) return;
      void this.paymentEl.submitPayment().finally(() => {
        if (!this.placed && !this.submission.paymentCommitted) {
          this.submission.release(false, this.registrationError);
        }
      });
      return;
    }
    if (!this.submission.claim()) return;
    this.registrationError = "";
    const total = ev.price * this.qty;
    if (total === 0) {
      // Deliberately stays claimed: the registration is placed, and releasing
      // here would let the second half of a double-click place it again before
      // `placed` has re-rendered the CTA away.
      void this.completeFreeRegistration(ev);
      return;
    }
    // Real payment in a light-DOM overlay (Stripe Elements + Connect charge).
    const el = document.createElement("zipper-payment") as ZipperPayment;
    el.runtime = this.runtime;
    el.inline = true;
    el.stripe = this.config?.stripe;
    el.orgId = this.config?.orgId ?? 0;
    el.accent = this.config?.theme?.accent ?? "#4F9DB5";
    el.requirePhone = this.requirePhone;
    el.requiredSignupFields = this.config?.requiredSignupFields;
    el.currency = this.config?.currency ?? "";
    el.heading = `Register · ${ev.title}`;
    el.items = Array.from({ length: this.qty }, (_, i) => ({
      stripeProductId: ev.stripeProductId ?? "",
      amount: ev.price,
      quantity: 1,
      customDescription: `${ev.title} registration${this.qty > 1 ? ` (${i + 1}/${this.qty})` : ""}`,
    }));
    el.customer = {
      ...this.reg,
      ...requiredProfilePayload(this.requiredProfile),
    };
    // After payment, register for the event (live data only): the buyer takes
    // the first ticket, and every extra ticket is registered against the named
    // guest who is actually taking it (E4 — see registerExtraGuest).
    el.savedCards = this.memberCtx?.savedCards ?? [];
    el.memberContext = this.memberCtx ?? undefined;
    const orgSlug = this.config?.orgSlug;
    if (orgSlug && ev.activityId) {
      const activityId = ev.activityId;
      type FormSubmission = { formId: number; formResponseId: string };
      let buyerForms: FormSubmission[] | null = null;
      const guestRosters = new Map<number, GuestRosterHandle>();
      const guestForms = new Map<number, FormSubmission[]>();

      // Every paid registration cites a known attendee. Save its forms under
      // that identity before Stripe moves money, so an ownerless response can
      // never strand a paid seat at registration finalization.
      if (ev.forms?.length) {
        el.beforeCharge = async () => {
          if (buyerForms === null) {
            let identity: FormSubmitIdentity;
            if (this.memberCtx) {
              identity = { kind: "member" };
            } else {
              const roster = await el.ensureGuestRoster({ retryFailed: true });
              if (!roster) {
                throw new Error(
                  "Could not save your details — please try again.",
                );
              }
              identity = {
                kind: "guest",
                rosterId: roster.rosterId,
                checkoutToken: roster.checkoutToken,
              };
            }
            buyerForms = await this.submitEventForms(ev, identity, this.reg);
          }

          for (let i = 1; i < this.qty; i++) {
            const guest = this.guests[i - 1];
            if (!guest || !/\S+@\S+\.\S+/.test(guest.email.trim())) {
              throw new Error("Add an email for each attendee before paying.");
            }
            let roster = guestRosters.get(i);
            if (!roster) {
              roster = await createGuestRoster(
                this.config!.orgId,
                { ...guest, isPrimaryCustomer: false },
                ...runtimeApiArguments(this.runtime),
              ).catch((error) => {
                if (isAccountExistsError(error)) {
                  throw new Error(
                    accountExistsForOtherMessage(
                      guest.email,
                      `${guest.firstName} ${guest.lastName}`.trim(),
                    ),
                  );
                }
                throw error;
              });
              guestRosters.set(i, roster);
            }
            if (!guestForms.has(i)) {
              guestForms.set(
                i,
                await this.submitEventForms(
                  ev,
                  {
                    kind: "guest",
                    rosterId: roster.rosterId,
                    checkoutToken: roster.checkoutToken,
                  },
                  guest,
                ),
              );
            }
          }
        };
      }

      el.afterCharge = async (invoiceIds) => {
        if (ev.forms?.length && buyerForms === null) {
          throw new Error("Required forms were not saved before payment.");
        }
        const preparedBuyerForms = buyerForms ?? [];
        if (this.memberCtx) {
          await registerSelf(
            this.config!.orgId,
            orgSlug,
            ev.id,
            {
              activityId,
              stripeInvoiceId: invoiceIds[0],
              ...(preparedBuyerForms.length
                ? { formSubmissions: preparedBuyerForms }
                : {}),
            },
            ...runtimeApiArguments(this.runtime),
          );
        } else {
          await registerGuest(
            this.config!.orgId,
            orgSlug,
            ev.id,
            {
              activityId,
              stripeInvoiceId: invoiceIds[0],
              ...(preparedBuyerForms.length
                ? { formSubmissions: preparedBuyerForms }
                : {}),
            },
            ...runtimeApiArguments(this.runtime),
          );
        }
        for (let i = 1; i < this.qty; i++) {
          const guest = this.guests[i - 1];
          if (ev.forms?.length) {
            const roster = guestRosters.get(i);
            const submissions = guestForms.get(i);
            if (!roster || !submissions) {
              throw new Error("Attendee forms were not saved before payment.");
            }
            await registerGuest(
              this.config!.orgId,
              orgSlug,
              ev.id,
              {
                activityId,
                stripeInvoiceId: invoiceIds[i],
                purchasedForRosterId: roster.rosterId,
                formSubmissions: submissions,
              },
              ...runtimeApiArguments(this.runtime),
            );
          } else {
            await this.registerExtraGuest(
              orgSlug,
              ev.id,
              activityId,
              guest,
              invoiceIds[i],
            );
          }
        }
      };
    }
    el.addEventListener("zipper-pay-success", () => {
      this.submission.markPaymentCommitted();
      this.teardownPayment();
      this.placeRegistration(ev);
    });
    el.addEventListener("zipper-pay-close", () => this.teardownPayment());
    // Must be attached BEFORE append: the component fires its first
    // zipper-pay-ready(false) on its first update.
    el.addEventListener("zipper-pay-ready", (e) => {
      this.payReady = (
        e as CustomEvent<{ canSubmit: boolean }>
      ).detail.canSubmit;
    });
    el.addEventListener("zipper-pay-busy", (e) => {
      const detail = (e as CustomEvent<{ busy: boolean }>).detail;
      this.submission.setChildBusy(!!detail?.busy && !this.placed);
    });
    // Prefer the inline host in the Payment step. Fall back to document.body
    // when it isn't there (element not yet rendered/attached) rather than
    // returning — dropping the element here would abandon a charge the visitor
    // already committed to, which is far worse than a misplaced form.
    const host =
      this.renderRoot?.querySelector("#inline-pay-host") ?? document.body;
    host.appendChild(el);
    this.paymentEl = el;
    this.submission.paymentRequired();
  }

  /**
   * E4: an extra ticket belongs to the *named guest* who is taking it, not to a
   * second anonymous copy of the buyer. Before registering that seat we mint the
   * guest their own customer profile through the anon create-guest endpoint
   * (user + roster + Stripe customer, phone-capable), then cite that roster on
   * the guest's registration as `purchasedForRosterId` so the seat is
   * attributable to them.
   *
   * Two deliberate constraints:
   * - The guest roster must NOT ride on the charge. `charge-multiple-guest`
   *   fails closed when `purchasedForRosterId` is anyone but the payment
   *   method's own roster ("Guest checkout cannot purchase for a different
   *   roster member"), so the money stays on the buyer — which is also what the
   *   buyer's payment history should show.
   * - create-guest is keyed on email, so a guest who left the optional email
   *   blank cannot be given a distinct profile — reusing the buyer's address
   *   would merge the guest INTO the buyer's roster and rename them. Those
   *   tickets register exactly as before, with no beneficiary roster.
   *
   * Profile creation is best-effort: the card has already been charged by the
   * time this runs, so a failure here must never strand a paid ticket.
   */
  private async registerExtraGuest(
    orgSlug: string,
    eventSlug: string,
    activityId: string,
    guest: EventGuest | undefined,
    stripeInvoiceId?: string,
    formSubmissions?: { formId: number; formResponseId: string }[],
  ): Promise<void> {
    const orgId = this.config?.orgId ?? 0;
    const firstName = guest?.firstName.trim() ?? "";
    const lastName = guest?.lastName.trim() ?? "";
    const email = guest?.email.trim() ?? "";
    let purchasedForRosterId: number | undefined;
    if (orgId && firstName && lastName && /\S+@\S+\.\S+/.test(email)) {
      try {
        const roster = await createGuestRoster(
          orgId,
          {
            firstName,
            lastName,
            email,
            // See above — an extra ticket holder, not the registrant.
            isPrimaryCustomer: false,
          },
          ...runtimeApiArguments(this.runtime),
        );
        purchasedForRosterId = roster.rosterId;
      } catch {
        /* profile creation failed — still register the paid seat */
      }
    }
    await registerGuest(
      this.config!.orgId,
      orgSlug,
      eventSlug,
      {
        activityId,
        stripeInvoiceId,
        purchasedForRosterId,
        ...(formSubmissions?.length ? { formSubmissions } : {}),
      },
      ...runtimeApiArguments(this.runtime),
    );
  }

  /**
   * Persist every event form before registering an attendee. The event checkout
   * currently represents a waiver as a single explicit consent checkbox, so
   * that consent becomes a non-empty answer for each required question in the
   * provider-authored form. The server remains authoritative: registration
   * cites the resulting response ids and re-validates every required form.
   */
  private async submitEventForms(
    ev: MockEvent,
    identity: FormSubmitIdentity,
    attendee?: { firstName?: string; lastName?: string; email?: string },
  ): Promise<{ formId: number; formResponseId: string }[]> {
    const orgId = this.config?.orgId ?? 0;
    if (!orgId || !ev.forms?.length) return [];

    const submissions: { formId: number; formResponseId: string }[] = [];
    for (const definition of ev.forms) {
      const form =
        this.waiverForm?.id === definition.formId
          ? this.waiverForm
          : await fetchAppointmentForm(orgId, definition.formId);
      if (!form) {
        throw new Error(
          `Required form ${definition.formId} could not be loaded`,
        );
      }
      const fullName = [attendee?.firstName, attendee?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      const answers = form.questions
        .filter((question) => question.required)
        .map((question) => ({
          questionKey: question.key,
          value:
            question.type === "email"
              ? attendee?.email?.trim() || "accepted@guest.invalid"
              : question.type === "first-name"
                ? attendee?.firstName?.trim() || "Guest"
                : question.type === "last-name"
                  ? attendee?.lastName?.trim() || "Guest"
                  : question.type === "signature"
                    ? fullName || "Accepted"
                    : "Accepted",
        }));
      const formResponseId = await submitAppointmentForm(
        orgId,
        definition.formId,
        answers,
        identity,
      );
      if (!formResponseId) {
        throw new Error(`Required form ${definition.formId} was not saved`);
      }
      submissions.push({ formId: definition.formId, formResponseId });
    }
    return submissions;
  }

  /** Real $0 checkout: persist identity/forms and register every ticket. */
  private async completeFreeRegistration(ev: MockEvent): Promise<void> {
    const orgId = this.config?.orgId ?? 0;
    const orgSlug = this.config?.orgSlug;
    if (!orgId || !orgSlug || !ev.activityId) {
      this.registrationError = "Registration is unavailable. Please try again.";
      this.submission.fail(this.registrationError);
      return;
    }

    try {
      if (
        ev.forms?.length &&
        this.guests.some((guest) => !/\S+@\S+\.\S+/.test(guest.email.trim()))
      ) {
        throw new Error("Add an email for each attendee before registering.");
      }

      let buyerRosterId: number | undefined;
      let buyerForms: { formId: number; formResponseId: string }[];
      if (this.memberCtx) {
        buyerForms = await this.submitEventForms(
          ev,
          { kind: "member" },
          this.reg,
        );
      } else {
        const buyer = await createGuestRoster(
          orgId,
          { ...this.reg, ...requiredProfilePayload(this.requiredProfile) },
          ...runtimeApiArguments(this.runtime),
        ).catch((e) => {
          // This address is already a member here, so the anonymous path is
          // closed to it and retrying can only fail the same way. Say we
          // recognised it and open sign-in, exactly as the class checkout
          // does — nothing has been registered at this point.
          if (isAccountExistsError(e)) {
            this.registrationError = accountExistsMessage(
              this.reg.email,
              "register",
            );
            this.openSignInModal();
            this.submission.fail(this.registrationError);
            throw new AlreadyReportedRegistrationError();
          }
          throw e;
        });
        buyerRosterId = buyer.rosterId;
        buyerForms = await this.submitEventForms(
          ev,
          {
            kind: "guest",
            rosterId: buyer.rosterId,
            checkoutToken: buyer.checkoutToken,
          },
          this.reg,
        );
      }

      const preparedGuests: {
        rosterId: number;
        forms: { formId: number; formResponseId: string }[];
      }[] = [];
      for (let i = 1; i < this.qty; i++) {
        const guest = this.guests[i - 1];
        if (!guest) throw new Error(`Missing attendee ${i + 1}`);
        const roster = await createGuestRoster(
          orgId,
          // An extra ticket holder, not the registrant — the org's
          // required-signup-fields policy is asked of the buyer only.
          { ...guest, isPrimaryCustomer: false },
          ...runtimeApiArguments(this.runtime),
        ).catch((e) => {
          // The recognised email is the TICKET HOLDER's, not the buyer's, so
          // sign-in is not the remedy: it would sign the buyer in as someone
          // else. Name whose address we recognised so they can fix that one
          // attendee instead of re-reading a "registration failed".
          if (isAccountExistsError(e)) {
            this.registrationError = accountExistsForOtherMessage(
              guest.email,
              `${guest.firstName} ${guest.lastName}`.trim(),
            );
            this.submission.fail(this.registrationError);
            throw new AlreadyReportedRegistrationError();
          }
          throw e;
        });
        const guestForms = await this.submitEventForms(
          ev,
          {
            kind: "guest",
            rosterId: roster.rosterId,
            checkoutToken: roster.checkoutToken,
          },
          guest,
        );
        preparedGuests.push({ rosterId: roster.rosterId, forms: guestForms });
      }

      // Prepare every attendee before registering the first seat. A missing
      // guest identity or form must not leave a partially registered order.
      if (this.memberCtx) {
        await registerSelf(
          orgId,
          orgSlug,
          ev.id,
          {
            activityId: ev.activityId,
            formSubmissions: buyerForms,
          },
          ...runtimeApiArguments(this.runtime),
        );
      } else {
        await registerGuest(
          orgId,
          orgSlug,
          ev.id,
          {
            activityId: ev.activityId,
            purchasedForRosterId: buyerRosterId,
            formSubmissions: buyerForms,
          },
          ...runtimeApiArguments(this.runtime),
        );
      }

      for (const guest of preparedGuests) {
        await registerGuest(
          orgId,
          orgSlug,
          ev.id,
          {
            activityId: ev.activityId,
            purchasedForRosterId: guest.rosterId,
            formSubmissions: guest.forms,
          },
          ...runtimeApiArguments(this.runtime),
        );
      }
      this.placeRegistration(ev);
    } catch (error) {
      // The recognised-email branches above already said something specific
      // (and failed the submission); a generic overwrite here would undo it.
      if (error instanceof AlreadyReportedRegistrationError) return;
      this.registrationError =
        error instanceof Error && error.message
          ? error.message
          : "Registration failed. Please try again.";
      this.submission.fail(this.registrationError);
    }
  }

  /**
   * Mount the embedded payment form as soon as the Payment step opens.
   *
   * Load-bearing, not an optimisation: the CTA is disabled until the form
   * reports it is chargeable (`payReady`, via `zipper-pay-ready`). If we only
   * mounted on CTA click — as the old modal flow did — the CTA could never be
   * enabled and the step would dead-end.
   *
   * Base declares `updated` public, so this must not narrow to `protected`,
   * and it must call `super.updated`.
   */
  updated(changed: Map<string, unknown>): void {
    super.updated(changed);
    this.cancelClosingRefresh?.();
    if (this.isConnected) {
      this.cancelClosingRefresh = scheduleRegistrationClosingRefresh(this.events.filter((event) => !event.program && !event.externalTickets), () => this.requestUpdate());
    }
    if (this.evtStep !== 2 || this.placed) return;
    const ev = this.events.find((e) => e.id === this.openId);
    if (!ev || ev.price * this.qty <= 0) return;
    if (!this.paymentEl) {
      const authorityKey = this.discoveryAuthorityKey();
      const selectionEpoch = this.selectionEpoch;
      const catalogRequest = this.catalogRequest;
      void this.updateComplete.then(() => {
        if (authorityKey !== this.discoveryAuthorityKey() ||
            selectionEpoch !== this.selectionEpoch ||
            catalogRequest !== this.catalogRequest ||
            this.openId !== ev.id || this.evtStep !== 2 || this.placed) return;
        return this.submitRegistration(ev);
      });
      return;
    }
    // Ticket count can change while the step is open.
    this.paymentEl.items = Array.from({ length: this.qty }, (_, i) => ({
      stripeProductId: ev.stripeProductId ?? "",
      amount: ev.price,
      quantity: 1,
      customDescription: `${ev.title} registration${
        this.qty > 1 ? ` (${i + 1}/${this.qty})` : ""
      }`,
    }));
  }

  private teardownPayment(): void {
    this.paymentEl?.remove();
    this.paymentEl = null;
    this.payReady = false;
    this.submission.setChildBusy(false);
    if (!this.placed && !this.submission.paymentCommitted) {
      this.submission.release(false);
    }
  }

  disconnectedCallback(): void {
    this.catalogRequest++;
    this.loadedAuthorityKey = "";
    super.disconnectedCallback();
    this.cancelClosingRefresh?.();
    this.cancelClosingRefresh = undefined;
    this.teardownPayment();
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (this.config && !this.loadedAuthorityKey) this.requestUpdate("config", null);
  }

  private placeRegistration(ev: MockEvent): void {
    this.submission.succeed();
    this.dispatchEvent(
      new CustomEvent("zipper-register-complete", {
        detail: { eventId: ev.id, title: ev.title, tickets: this.qty },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "zipper-events": ZipperEvents;
  }
}
